<template>
  <div class="c-domain-setup-form">
    <el-form
        ref="form"
        :model="domainSetupForm"
        label-position="top"
        label-width="120px"
        @submit.native.prevent>
      <el-row :gutter="20">
        <el-col :span="11">
          <el-form-item label="Domain">
            <el-select
                v-model="currentDomainID"
                class="w-full gray"
                placeholder="Choose a domain"
                default-first-option
                filterable>
              <el-option
                  v-for="domain in allDomains"
                  :key="domain.id"
                  :label="domain.url"
                  :value="domain.id"/>
            </el-select>
          </el-form-item>
        </el-col>
        <!-- Radio group for selecting pages mode -->
        <el-col :span="12" :offset="1" class="target-settings mb-4">
          <el-form-item label="Display Mode">
            <el-radio-group v-model="domainSetupForm.pages_mode">
              <el-radio-button label="all">All pages</el-radio-button>
              <el-radio-button label="specific">Include on pages</el-radio-button>
              <el-radio-button label="exclude">Exclude from pages</el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-col>
      </el-row>
      <!-- Input for adding subdirectories; shown only when mode is not "all" -->
      <el-row v-if="domainSetupForm.pages_mode !== 'all'" :gutter="20">
        <el-col :span="12" :offset="12">
          <el-form-item>
            <div class="flex flex-col items-start gap-y-2">
              <div class="w-full flex gap-x-2">
                <el-select v-model="inputMatchType" class="w-48 gray" placeholder="Match type">
                  <el-option label="Equals" value="equals"></el-option>
                  <el-option label="Starts with" value="starts_with"></el-option>
                  <el-option label="Contains" value="contains"></el-option>
                  <el-option label="Ends with" value="ends_with"></el-option>
                </el-select>
                <el-input
                    ref="saveTagInput"
                    v-model="inputValue"
                    class="grow gray"
                    placeholder="/subdirectory"
                    @keyup.enter.native="handleInputConfirm"
                    @blur="handleInputConfirm"/>
                <el-button
                    class="button-new-url"
                    size="medium"
                    @click="handleInputConfirm">
                  Add
                </el-button>
              </div>
              <!-- Validation error -->
              <div v-if="pathValidationError" class="text-onvocado-red text-xs">
                {{ pathValidationError }}
              </div>
              <!-- Display added subdirectories with their match type -->
              <el-tag
                  v-for="(subdir, index) in alphabeticallySortedSubdirectories"
                  :key="index"
                  class="!text-body-black"
                  closable
                  @close="handleClose(subdir, index)">
                /{{ subdir.path }} ({{ formatMatch(subdir.match) }})
              </el-tag>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
export default {
  name: 'DomainSetupForm',
  data() {
    return {
      enableUpdate: false,
      inputValue: '',
      // New property for match type when adding a subdirectory; default is "equals"
      inputMatchType: 'equals',
      pathValidationError: '',
      domainSetupForm: {
        domain: '',
        domainID: '',
        // pages_mode can be "all", "specific", or "exclude"
        pages_mode: 'all',
        // A unified array for subdirectories; each entry is { path: string, match: string }
        subdirectories: [],
      },
    }
  },
  computed: {
    allDomains() {
      return this.$store.state.domain.domains
    },
    campaign() {
      return this.$store.getters['campaign/targetCampaign']
    },
    currentDomainID: {
      get() {
        return this.domainSetupForm.domainID
      },
      set(domainID) {
        const domainObj = this.allDomains.find(domain => domain.id === domainID)
        if (domainObj) {
          this.domainSetupForm.domain = domainObj.url
          this.domainSetupForm.domainID = domainObj.id
        } else {
          console.error('Selected domain ID does not exist in allDomains')
        }
      },
    },
    alphabeticallySortedSubdirectories() {
      return this.domainSetupForm.subdirectories
          ? this.domainSetupForm.subdirectories.slice().sort((a, b) => a.path.localeCompare(b.path))
          : []
    },
  },
  watch: {
    'campaign.domain'(newVal) {
      if (this.domainSetupForm.domain !== newVal) {
        this.domainSetupForm.domain = newVal
      }
    },
    'campaign.domainID'(newVal) {
      if (this.domainSetupForm.domainID !== newVal) {
        this.domainSetupForm.domainID = newVal
      }
    },
    // Update pages_mode when campaign data changes.
    'campaign.pages_mode'(newVal) {
      if (this.domainSetupForm.pages_mode !== newVal) {
        this.domainSetupForm.pages_mode = newVal
      }
    },
    // Update the unified subdirectories array when campaign data changes.
    'campaign.subdirectories'(newVal) {
      if (JSON.stringify(this.domainSetupForm.subdirectories) !== JSON.stringify(newVal)) {
        // If the campaign provides a match type, use it; otherwise default to "equals"
        this.domainSetupForm.subdirectories = newVal.map(subdir => ({
          path: subdir.path,
          match: subdir.match || 'equals',
        }))
      }
    },
    domainSetupForm: {
      handler() {
        if (this.enableUpdate) {
          this.$emit('update', this.domainSetupForm)
        }
      },
      deep: true,
      immediate: false,
    },
  },
  created() {
    this.setFormValues()
    this.$nextTick(() => {
      this.enableUpdate = true
    })
  },
  methods: {
    setFormValues() {
      const newValue = JSON.parse(JSON.stringify(this.campaign))
      this.domainSetupForm.domain = newValue.domain
      this.domainSetupForm.domainID = newValue.domainID
      this.domainSetupForm.pages_mode = newValue.pages_mode || 'all'
      this.domainSetupForm.subdirectories = newValue.subdirectories
          ? newValue.subdirectories.map(subdir => ({
            path: subdir.path,
            match: subdir.match || 'equals',
          }))
          : []
    },
    handleClose(subdir, index) {
      this.domainSetupForm.subdirectories.splice(index, 1)
    },
    formatMatch(match) {
      // Capitalize and replace underscores with spaces for display
      return match.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },
    isValidPath(value) {
      if (value.includes(' ')) {
        this.pathValidationError = 'Page cannot contain spaces.'
        return false
      }
      const regex = /^[a-zA-Z0-9_\\/-]+$/
      if (!regex.test(value)) {
        this.pathValidationError = 'Invalid page. Only letters, numbers, underscores, hyphens, and slashes are allowed.'
        return false
      }
      this.pathValidationError = ''
      return true
    },
    handleInputConfirm() {
      let inputValue = this.inputValue.trim()
      if (inputValue) {
        inputValue = inputValue.replace(/^\/+|\/+$/g, '')
        if (!inputValue) {
          this.pathValidationError = 'Page cannot be empty.'
          return
        }
        if (!this.isValidPath(inputValue)) {
          return
        }
        // Check for duplicate entry by both path and match type.
        if (!this.domainSetupForm.subdirectories.some(
            subdir => subdir.path === inputValue && subdir.match === this.inputMatchType)) {
          this.domainSetupForm.subdirectories.push({
            path: inputValue,
            match: this.inputMatchType,
          })
        }
      }
      this.inputValue = ''
    },
  },
}
</script>

<style lang="scss">
.c-domain-setup-form {
  .el-form-item {
    width: 100%;
  }

  .el-switch__label span {
    margin-bottom: 10px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1em;
  }

  .target-settings {
    .el-tag {
      display: block;
      margin: 5px 0;
    }

    .el-button {
      padding: 11px 20px;
    }
  }
}
</style>